// Angular
import { Injectable } from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';

// Component Library
import { getFsxAutocompleteSelectedValue } from 'fsx-component-library';

// Local
import { isIndividualEntityType } from '@shared/utils/entity-type.utils';
import { IRepresentationFormGroup } from '@feature/filing/shared/services/representation-form.service';
import { findFormControl } from '@feature/filing/shared/utils/find-form-control';
import { UsesAddressForm } from '@feature/filing/shared/utils/uses-address-form';
import { AutocompleteRequiredValidator } from '@shared/validators/autocomplete-required-validator';
import { isEmailValidator } from '@shared/validators/is-email.validator';
import { minTrimmedLengthValidator } from '@shared/validators/min-trimmed-length.validator';
import { phoneNumberValidator } from '@shared/validators/phone-number.validator';
import { fieldRequiredIfOtherFilledValidator } from '@shared/validators/require-field-based-on-another.validator';
import { CASE_SSN_AND_TIN_REGEX } from '@shared/validators/validators';

export enum PartyTypeEnum {
  SENDING_PARTY = 1,
  ADDITIONAL_PARTY = 0,
  SERVICE_PARTY = 2,
}

@Injectable({
  providedIn: 'root',
})
export class PartiesFormService extends UsesAddressForm {
  /**
   * Updates the validators for the new party form based on the selected entity type
   * Individual - requires first and last name, org name not required
   * Organization - requires org name, first and last name not required
   */
  updatePartyFormValidators(form: FormGroup): void {
    const value = getFsxAutocompleteSelectedValue(
      form.get('entityType')?.value,
    ) as number | undefined;

    const orgNameControl = form.get('orgName');
    const firstNameControl = form.get('firstName');
    const lastNameControl = form.get('lastName');

    const ssnControl = form.get('ssn');
    const dobControl = form.get('dob');

    if (isIndividualEntityType(value)) {
      // Individual selected
      orgNameControl?.clearValidators();
      orgNameControl?.setValue('');
      firstNameControl?.setValidators([
        Validators.required,
        minTrimmedLengthValidator(2),
        Validators.maxLength(50),
      ]);
      lastNameControl?.setValidators([
        Validators.required,
        minTrimmedLengthValidator(2),
        Validators.maxLength(50),
      ]);
    } else if (value != null) {
      // Any non-individual entity type (Organization, Agency, etc.)
      orgNameControl?.setValidators([
        Validators.required,
        Validators.maxLength(100),
      ]);
      firstNameControl?.clearValidators();
      firstNameControl?.setValue('');
      lastNameControl?.clearValidators();
      lastNameControl?.setValue('');

      ssnControl?.clearValidators();
      ssnControl?.setValue('');
      ssnControl?.updateValueAndValidity();

      dobControl?.clearValidators();
      dobControl?.setValue(null);
      dobControl?.updateValueAndValidity();
    }

    orgNameControl?.updateValueAndValidity();
    firstNameControl?.updateValueAndValidity();
    lastNameControl?.updateValueAndValidity();
  }

  markCounselRequired(isCounselRequired: boolean, form: FormGroup) {
    const repArray = form.get('representation') as FormArray<
      FormGroup<IRepresentationFormGroup>
    >;

    repArray.controls.forEach(repGroup => {
      const counselTypeControl = repGroup.get('counselType');
      if (isCounselRequired) {
        counselTypeControl.setValidators([
          Validators.required,
          AutocompleteRequiredValidator(),
        ]);
      } else {
        counselTypeControl.clearValidators();
      }

      counselTypeControl.updateValueAndValidity({ onlySelf: true });
    });
  }

  /**
   * A method to handle the different OPF SSN settings for the court profile
   */
  markSsnRequired(value: boolean, form: FormGroup): void {
    const ssnControl = findFormControl(form, 'ssn');
    if (value === true) {
      ssnControl?.setValidators([
        Validators.required,
        Validators.pattern(CASE_SSN_AND_TIN_REGEX),
      ]);
    } else if (value === false) {
      ssnControl?.clearValidators();
      ssnControl?.setValidators([Validators.pattern(CASE_SSN_AND_TIN_REGEX)]);
    } else {
      ssnControl?.clearValidators();
      ssnControl?.setValue(null);
    }
    ssnControl?.updateValueAndValidity();
  }

  /**
   * A method to handle the different OPF DOB settings for the court profile
   */
  markDobRequired(value: boolean, form: FormGroup) {
    const dobControl = findFormControl(form, 'dob');
    if (value === true) {
      dobControl?.setValidators([Validators.required]);
    } else if (value === false) {
      dobControl?.clearValidators();
    } else {
      dobControl?.clearValidators();
      dobControl?.setValue(null);
    }
    dobControl?.updateValueAndValidity();
  }

  /**
   * Method to handle different OPF Phone number settings
   */
  markPhoneRequired(value: boolean, form: FormGroup) {
    const phoneControl = findFormControl(form, 'phone');
    const extControl = findFormControl(form, 'ext');
    if (value === true) {
      phoneControl?.setValidators([
        Validators.required,
        phoneNumberValidator(),
        fieldRequiredIfOtherFilledValidator(extControl),
      ]);
    } else if (value === false) {
      phoneControl?.clearValidators();
      phoneControl?.setValidators([
        phoneNumberValidator(),
        fieldRequiredIfOtherFilledValidator(extControl),
      ]);
    }
    phoneControl?.updateValueAndValidity();
  }

  /**
   * Method to handle different OPF Email settings
   */
  markEmailRequired(value: boolean, form: FormGroup): void {
    const emailControl = findFormControl(form, 'email');
    if (value) {
      emailControl?.setValidators([Validators.required, isEmailValidator()]);
    } else {
      emailControl?.setValidators([isEmailValidator()]);
    }
    emailControl?.updateValueAndValidity();
  }
}
